import { isNil } from 'ramda';
import {
  DeclaredPowerAndEnergyRating,
  IPowerAndEnergyRating,
} from 'src/net-availability/types';
import { BaseError } from 'src/types/base-error';
import { Result } from 'src/types/result';
import { getErrorResult, getResult } from 'src/utils/result/utils';
import { fixTo3DecimalPlaces } from 'src/utils/rounding';

export class UndeclaredMaxImportPowerError extends BaseError {
  kind = 'UndeclaredMaxImportPowerError' as const;
  constructor(
    minDeclared: DeclaredPowerAndEnergyRating,
    items: DeclaredPowerAndEnergyRating[],
  ) {
    super(`No declared maxImportPower found`, {
      context: JSON.stringify({ minDeclared, items }),
    });
  }
}

export class UndeclaredMaxExportPowerError extends BaseError {
  kind = 'UndeclaredMaxExportPowerError' as const;
  constructor(
    minDeclared: DeclaredPowerAndEnergyRating,
    items: DeclaredPowerAndEnergyRating[],
  ) {
    super(`No declared maxExportPower found`, {
      context: JSON.stringify({ minDeclared, items }),
    });
  }
}

export class UndeclaredMaxEnergyCapacityError extends BaseError {
  kind = 'UndeclaredMaxEnergyCapacityError' as const;
  constructor(
    minDeclared: DeclaredPowerAndEnergyRating,
    items: DeclaredPowerAndEnergyRating[],
  ) {
    super(`No declared maxEnergyCapacity found`, {
      context: JSON.stringify({ minDeclared, items }),
    });
  }
}

const getMinDefined = (values: (number | undefined)[]): number | undefined =>
  values.filter((it) => !isNil(it)).sort((a, b) => a - b)[0] ?? undefined;

export const getMinDeclaredPowerAndEnergyRating = (
  items: DeclaredPowerAndEnergyRating[],
): DeclaredPowerAndEnergyRating => {
  const minDeclaredPowerAndEnergyRating: DeclaredPowerAndEnergyRating = {
    energyCapacity: getMinDefined(items.map((it) => it.energyCapacity)),
    maxImportPower: getMinDefined(items.map((it) => it.maxImportPower)),
    maxExportPower: getMinDefined(items.map((it) => it.maxExportPower)),
  };

  return minDeclaredPowerAndEnergyRating;
};

export const getMinPowerAndEnergyRating = (
  items: DeclaredPowerAndEnergyRating[],
): Result<
  | UndeclaredMaxEnergyCapacityError
  | UndeclaredMaxExportPowerError
  | UndeclaredMaxImportPowerError,
  IPowerAndEnergyRating
> => {
  const minDeclared = getMinDeclaredPowerAndEnergyRating(items);

  if (minDeclared.maxImportPower === undefined) {
    return getErrorResult(
      new UndeclaredMaxImportPowerError(minDeclared, items),
    );
  }

  if (minDeclared.maxExportPower === undefined) {
    return getErrorResult(
      new UndeclaredMaxExportPowerError(minDeclared, items),
    );
  }

  if (minDeclared.energyCapacity === undefined) {
    return getErrorResult(
      new UndeclaredMaxEnergyCapacityError(minDeclared, items),
    );
  }

  return getResult(minDeclared as IPowerAndEnergyRating);
};

export const getAggregatedPowerAndEnergyRating = (
  items: IPowerAndEnergyRating[],
): IPowerAndEnergyRating => {
  const aggregatedPowerAndEnergyRating: IPowerAndEnergyRating = items.reduce(
    (acc, it) => ({
      energyCapacity: fixTo3DecimalPlaces(
        acc.energyCapacity + it.energyCapacity,
      ),
      maxImportPower: fixTo3DecimalPlaces(
        acc.maxImportPower + it.maxImportPower,
      ),
      maxExportPower: fixTo3DecimalPlaces(
        acc.maxExportPower + it.maxExportPower,
      ),
    }),
    {
      energyCapacity: 0,
      maxImportPower: 0,
      maxExportPower: 0,
    } satisfies IPowerAndEnergyRating,
  );

  return aggregatedPowerAndEnergyRating;
};
